/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  IDatafeedChartApi,
  IExternalDatafeed,
  ResolutionString,
  Timezone,
} from 'modules/tw-chart/datafeed-api';
import * as Sentry from '@sentry/react';
import { CandleStickService } from 'api/services/httpServices/CandleSticksService';
import Big from 'big.js';
import { twSubscriptions } from 'modules/tw-chart/subscriptionStore';
import { Chains, dexName, Protocols } from 'api/types/httpsTypes/d-wallets';
import { SolanaOHLCItem } from 'api/types/httpsTypes/candle-sticks';
import { safeBig } from 'utils/FormatNumber';

export const secondsMultipliers: string[] = [
  '1',
  '5',
  '15',
  '30',
  '60',
  '180',
  '300',
  '900',
  '1800',
  '3600',
  '14400',
  '86400',
];

export const supportedResolutions = [
  '1S',
  '5S',
  '15S',
  '30S',
  '1',
  '3',
  '5',
  '15',
  '30',
  '60',
  '240',
  '360',
  '720',
  '1D',
] as ResolutionString[];

const configurationData = {
  supports_marks: false,
  supports_timescale_marks: false,
  supports_time: false,
  has_seconds: true,
  seconds_multipliers: secondsMultipliers,
  supported_resolutions: supportedResolutions,
};

export function calculatePriceScale(currentPrice: number): number {
  let orderOfMagnitude: number = Math.floor(Math.log10(currentPrice)) - 3;
  orderOfMagnitude = Math.max(orderOfMagnitude, -14); // Minimum order of magnitude to handle small values

  return Big(10).pow(-orderOfMagnitude).toNumber();
}

export const createDatafeed = (
  chain: Chains,
  dex: Protocols | undefined,
  referencePrice?: number,
  currencyIsUsd?: boolean,
  isPriceShown?: boolean,
  totalTokensSupply?: number
): IDatafeedChartApi & IExternalDatafeed => {
  const multiplier = isPriceShown ? 1 : totalTokensSupply ?? 0;
  return {
    onReady: (callback) => {
      setTimeout(() => callback(configurationData)); // callback must be called asynchronously
    },
    searchSymbols: async () => {},
    resolveSymbol: (symbolFullName, onSymbolResolvedCallback) => {
      const [symbolPart] = symbolFullName.split('?');
      const [symbol] = symbolPart.split(':');

      setTimeout(() => {
        onSymbolResolvedCallback({
          session: '24x7',
          minmov: 1,
          pricescale: referencePrice
            ? calculatePriceScale(
                safeBig(referencePrice).mul(multiplier).toNumber()
              )
            : 100000000,
          has_intraday: true,
          has_daily: true,
          has_seconds: true,
          supported_resolutions: supportedResolutions,
          visible_plots_set: 'ohlcv',
          has_weekly_and_monthly: true,
          description: '',
          exchange: dexName(dex ?? Protocols.UniswapV2),
          format: 'price',
          full_name: symbol,
          listed_exchange: dexName(dex ?? Protocols.UniswapV2),
          timezone: Intl.DateTimeFormat().resolvedOptions()
            .timeZone as Timezone,
          name: symbol,
          ticker: symbolFullName,
          type: 'crypto',
        });
      }, 0);
    },
    getBars: async (
      symbolInfo,
      _resolution,
      periodParams,
      onHistoryCallback,
      onErrorCallback
    ) => {
      try {
        const [base] = symbolInfo.ticker!.split('?');
        const [, , poolAddress] = base.split(':');

        const result = await CandleStickService.getSolanaPoolCandleSticks({
          poolAddress,
          chain,
          limit: periodParams.countBack,
          toTimestamp: periodParams.to,
          fromTimestamp: periodParams.from,
          interval: solanaResolutionToTimeframe(_resolution),
          currency: currencyIsUsd ? 'usd' : 'quote',
        });

        const bars = (result as SolanaOHLCItem[])
          .map(({ t, o, h, l, c, v }) => ({
            time: t * 1000, // convert to ms
            open: safeBig(o).mul(multiplier).toNumber(),
            high: safeBig(h).mul(multiplier).toNumber(),
            low: safeBig(l).mul(multiplier).toNumber(),
            close: safeBig(c).mul(multiplier).toNumber(),
            volume: safeBig(v).toNumber(),
          }))
          .reverse();

        onHistoryCallback(bars, {
          noData: bars.length === 0,
        });
      } catch (e) {
        Sentry.captureException(e);
        onErrorCallback('Error');
      }
    },

    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscribeUID
    ) => {
      const [base, query] = symbolInfo.ticker!.split('?');
      const [, , poolAddress] = base.split(':');

      const subscriptionParams = {
        chain,
        poolAddress,
        interval: solanaResolutionToTimeframe(resolution),
        currency: currencyIsUsd ? 'usd' : 'quote',
        isPriceShown: !!isPriceShown,
      };

      twSubscriptions.subscribe(
        subscribeUID,
        subscriptionParams,
        onRealtimeCallback
      );
    },

    unsubscribeBars: (subscriberUID) => {
      twSubscriptions.unsubscribe(subscriberUID);
    },
  };
};

export enum ChartIntervals {
  OneSecond = '1s',
  FiveSeconds = '5s',
  FifteenSeconds = '15s',
  ThirtySeconds = '30s',
  OneMinute = '1m',
  ThreeMinutes = '3m',
  FiveMinutes = '5m',
  FifteenMinutes = '15m',
  ThirtyMinutes = '30m',
  OneHour = '1h',
  FourHours = '4h',
  SixHours = '6h',
  TwelveHours = '12h',
  TwentyFourHour = '1d',
}

export function solanaResolutionToTimeframe(
  resolution: ResolutionString
): ChartIntervals {
  switch (resolution) {
    case '1S':
      return ChartIntervals.OneSecond;
    case '5S':
      return ChartIntervals.FiveSeconds;
    case '15S':
      return ChartIntervals.FifteenSeconds;
    case '30S':
      return ChartIntervals.ThirtySeconds;
    case '1':
      return ChartIntervals.OneMinute;
    case '3':
      return ChartIntervals.ThreeMinutes;
    case '5':
      return ChartIntervals.FiveMinutes;
    case '15':
      return ChartIntervals.FifteenMinutes;
    case '30':
      return ChartIntervals.ThirtyMinutes;
    case '60':
      return ChartIntervals.OneHour;
    case '240':
      return ChartIntervals.FourHours;
    case '360':
      return ChartIntervals.SixHours;
    case '720':
      return ChartIntervals.TwelveHours;
    case '1D':
      return ChartIntervals.TwentyFourHour;
    default:
      return ChartIntervals.FifteenMinutes;
  }
}
